import { useMemo, useState } from 'react';
import { Bell, Mail, MessageSquare, RotateCcw, ShieldCheck, Webhook } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Tag from '../../components/ui/Tag';

const STORAGE_KEY = 'sentinel-command-alert-preferences-v1';

export const DEFAULT_ALERT_PREFERENCES = {
  channels: {
    email: true,
    teams: true,
    slack: false,
    webhook: false,
  },
  severityThreshold: 'high',
  digestCadence: 'hourly',
  sources: {
    vulnerabilities: true,
    sensitiveData: true,
    osint: true,
    cloud: true,
    ad: true,
    dast: true,
    compliance: false,
  },
  webhookUrl: '',
};

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const CADENCES = ['immediate', 'hourly', 'daily', 'weekly'];
const SOURCE_LABELS = {
  vulnerabilities: 'Vulnerabilities',
  sensitiveData: 'Sensitive Data',
  osint: 'OSINT',
  cloud: 'Cloud',
  ad: 'AD',
  dast: 'DAST',
  compliance: 'Compliance',
};

export function loadAlertPreferences(storage = window.localStorage) {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object'
      ? {
          ...DEFAULT_ALERT_PREFERENCES,
          ...parsed,
          channels: { ...DEFAULT_ALERT_PREFERENCES.channels, ...(parsed.channels ?? {}) },
          sources: { ...DEFAULT_ALERT_PREFERENCES.sources, ...(parsed.sources ?? {}) },
        }
      : DEFAULT_ALERT_PREFERENCES;
  } catch {
    return DEFAULT_ALERT_PREFERENCES;
  }
}

export function summarizeAlertPreferences(preferences) {
  const channels = Object.entries(preferences.channels ?? {}).filter(([, enabled]) => enabled).map(([key]) => key);
  const sources = Object.entries(preferences.sources ?? {}).filter(([, enabled]) => enabled).map(([key]) => key);
  return {
    enabledChannels: channels.length,
    enabledSources: sources.length,
    canDeliver: channels.length > 0 && sources.length > 0,
    needsWebhookUrl: Boolean(preferences.channels?.webhook) && !String(preferences.webhookUrl ?? '').trim(),
  };
}

function savePreferences(preferences) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
}

function FieldLabel({ children }) {
  return <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: 0, marginBottom: 6 }}>{children}</div>;
}

function ToggleRow({ icon: Icon, label, sub, enabled, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 7,
        border: `1px solid ${enabled ? C.grn : C.b1}`,
        background: enabled ? `${C.grn}10` : C.s2,
        color: C.t1,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} color={enabled ? C.grn : C.t3} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 800 }}>{label}</span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{sub}</span>
      </span>
      <Tag label={enabled ? 'On' : 'Off'} color={enabled ? C.grn : C.t3} />
    </button>
  );
}

export default function AlertPreferences() {
  const [preferences, setPreferences] = useState(() => loadAlertPreferences());
  const [savedAt, setSavedAt] = useState('');
  const summary = useMemo(() => summarizeAlertPreferences(preferences), [preferences]);

  const update = patcher => {
    setPreferences(current => {
      const next = patcher(current);
      savePreferences(next);
      setSavedAt(new Date().toLocaleTimeString());
      return next;
    });
  };

  const toggleChannel = key => update(current => ({
    ...current,
    channels: { ...current.channels, [key]: !current.channels[key] },
  }));

  const toggleSource = key => update(current => ({
    ...current,
    sources: { ...current.sources, [key]: !current.sources[key] },
  }));

  const reset = () => update(() => DEFAULT_ALERT_PREFERENCES);

  return (
    <Shell title="Alert Preferences" sub="Configure notification channels, thresholds, cadence, and source filters">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 18 }}>
            <CardHeader
              title="Notification Policy"
              sub="Model the MSP alert policy used for high-signal security events."
              action={<Tag label={summary.canDeliver ? 'Policy configured' : 'Needs channels'} color={summary.canDeliver ? C.grn : C.amb} />}
            />
            <div style={{ padding: '10px 12px', borderRadius: 7, border: `1px solid ${C.amb}33`, background: `${C.amb}10`, color: C.t1, fontSize: 11, fontFamily: FONTS.mono, lineHeight: 1.5, marginBottom: 14 }}>
              This is a local readiness surface. Backend delivery, escalation ownership, audit logs, and channel verification are still required before live client alerting.
            </div>
            <FieldLabel>Channels</FieldLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 10, marginBottom: 16 }}>
              <ToggleRow icon={Mail} label="Email" sub="Security digest and immediate alerts" enabled={preferences.channels.email} onToggle={() => toggleChannel('email')} />
              <ToggleRow icon={MessageSquare} label="Microsoft Teams" sub="MSP operations channel" enabled={preferences.channels.teams} onToggle={() => toggleChannel('teams')} />
              <ToggleRow icon={MessageSquare} label="Slack" sub="Partner workspace delivery" enabled={preferences.channels.slack} onToggle={() => toggleChannel('slack')} />
              <ToggleRow icon={Webhook} label="Webhook" sub="Signed outbound automation" enabled={preferences.channels.webhook} onToggle={() => toggleChannel('webhook')} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginBottom: 16 }}>
              <div>
                <FieldLabel>Severity threshold</FieldLabel>
                <select value={preferences.severityThreshold} onChange={event => update(current => ({ ...current, severityThreshold: event.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.b1}`, background: C.s2, color: C.t1 }}>
                  {SEVERITIES.map(sev => <option key={sev} value={sev}>{sev.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel>Digest cadence</FieldLabel>
                <select value={preferences.digestCadence} onChange={event => update(current => ({ ...current, digestCadence: event.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.b1}`, background: C.s2, color: C.t1 }}>
                  {CADENCES.map(cadence => <option key={cadence} value={cadence}>{cadence.toUpperCase()}</option>)}
                </select>
              </div>
            </div>

            <FieldLabel>Source filters</FieldLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {Object.entries(preferences.sources).map(([key, enabled]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleSource(key)}
                  style={{
                    border: `1px solid ${enabled ? C.sky : C.b1}`,
                    background: enabled ? `${C.sky}12` : C.s2,
                    color: enabled ? C.sky : C.t2,
                    borderRadius: 6,
                    padding: '6px 9px',
                    cursor: 'pointer',
                    fontSize: 11,
                    fontFamily: FONTS.mono,
                  }}
                >
                  {SOURCE_LABELS[key]}
                </button>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 18 }}>
            <CardHeader title="Webhook Policy" sub="Preview the delivery endpoint and signing requirement." />
            <FieldLabel>Webhook URL</FieldLabel>
            <input
              value={preferences.webhookUrl}
              onChange={event => update(current => ({ ...current, webhookUrl: event.target.value }))}
              placeholder="https://example.com/sentinel-command/webhook"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: `1px solid ${summary.needsWebhookUrl ? C.red : C.b1}`, background: C.s2, color: C.t1, marginBottom: 8 }}
            />
            <div style={{ fontSize: 10, color: summary.needsWebhookUrl ? C.red : C.t2, fontFamily: FONTS.mono }}>
              {summary.needsWebhookUrl ? 'Webhook delivery is enabled but no URL is configured.' : 'Live webhook delivery still requires HMAC signing and backend retry handling.'}
            </div>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 18 }}>
            <CardHeader title="Policy Summary" />
            {[
              ['Channels', String(summary.enabledChannels)],
              ['Sources', String(summary.enabledSources)],
              ['Threshold', preferences.severityThreshold.toUpperCase()],
              ['Cadence', preferences.digestCadence.toUpperCase()],
              ['Saved', savedAt || 'Local defaults'],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
                <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{label}</span>
                <span style={{ fontSize: 11, color: C.t1, fontFamily: FONTS.mono, textAlign: 'right' }}>{value}</span>
              </div>
            ))}
            <Button variant="ghost" onClick={reset} style={{ width: '100%', marginTop: 12 }}><RotateCcw size={13} />Reset Local Policy</Button>
          </Card>

          <Card style={{ padding: 18 }}>
            <CardHeader title="Readiness Checklist" />
            {[
              ['Channel policy model', true],
              ['Severity and cadence controls', true],
              ['Per-source filters', true],
              ['Backend delivery workers', false],
              ['Escalation schedules', false],
              ['Audit log and retry trail', false],
            ].map(([label, done]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${C.b2}` }}>
                <ShieldCheck size={14} color={done ? C.grn : C.amb} />
                <span style={{ flex: 1, fontSize: 11, color: C.t1, fontFamily: FONTS.mono }}>{label}</span>
                <Tag label={done ? 'Ready' : 'Needed'} color={done ? C.grn : C.amb} />
              </div>
            ))}
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Bell size={16} color={C.sky} />
              <div style={{ fontSize: 12, color: C.t1, fontWeight: 800 }}>Large-client note</div>
            </div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.55 }}>
              This closes the placeholder gap for policy design, but live readiness still depends on verified delivery, escalation ownership, and auditability.
            </div>
          </Card>
        </div>
      </div>
    </Shell>
  );
}
